import { useLayoutEffect, useMemo, useRef, useState } from "react";

import { useTranslate } from "@similarweb/i18n-provider";
import { Table } from "@similarweb/pro-logic-components";
import { Button } from "@similarweb/ui-components";
import { Flex } from "@similarweb/ui-components/foundations";

import type { TeamUser } from "../../types/teams.types";
import { teamMemberCountryLabel } from "../../utils/teamMemberCountryLabel";

import { getTeamMembersColumns, type TeamUserTableRow } from "./columns";
import { SELECTION_COLUMN_WIDTH, StyledTableCardWrapper } from "./styles";

const PAGE_SIZE = 20;

/** Fallback until the real table-top height is measured (filters + selection rows). */
const FALLBACK_TABLE_TOP_STICKY_OFFSET_PX = 110;
/** Matches the default scroll root used by `@similarweb/pro-logic-components` Table (`stickyContainerClass`). */
const TABLE_STICKY_SCROLL_CONTAINER_CLASS = "sw-layout-scrollable-element";

type TeamMembersTableProps = {
    members: readonly TeamUser[];
    currentUserId?: number;
    selectedUserIds: number[];
    onSelectionChange: (ids: number[]) => void;
    onAddMembers: () => void;
    onRemoveSelected: () => void;
    isRemoving: boolean;
};

export const TeamMembersTable = ({
    members,
    currentUserId,
    selectedUserIds,
    onSelectionChange,
    onAddMembers,
    onRemoveSelected,
    isRemoving,
}: TeamMembersTableProps) => {
    const { translate } = useTranslate();
    const [searchTerm, setSearchTerm] = useState("");
    const tableWrapperRef = useRef<HTMLDivElement>(null);
    const [tableTopStickyOffsetPx, setTableTopStickyOffsetPx] = useState(FALLBACK_TABLE_TOP_STICKY_OFFSET_PX);

    // The toolbar height is constant (filters row + the always-rendered, fixed-height
    // selection row), so a single measurement after mount is enough to align the sticky
    // column header beneath it.
    useLayoutEffect(() => {
        const headerEl = tableWrapperRef.current?.querySelector<HTMLElement>('[data-automation="table-top-header"]');
        if (headerEl) {
            setTableTopStickyOffsetPx(Math.ceil(headerEl.getBoundingClientRect().height));
        }
    }, []);

    const tableData = useMemo<TeamUserTableRow[]>(() => {
        const trimmedSearch = searchTerm.trim().toLowerCase();
        const filtered = trimmedSearch
            ? members.filter((member) =>
                  [member.name, member.email, member.jobTitle, teamMemberCountryLabel(member.country)].some((value) =>
                      (value ?? "").toLowerCase().includes(trimmedSearch),
                  ),
              )
            : members;
        return filtered.map((member) => ({ ...member, key: member.userId }));
    }, [members, searchTerm]);

    const columnConfig = useMemo(() => getTeamMembersColumns(translate, currentUserId), [translate, currentUserId]);

    return (
        <StyledTableCardWrapper
            ref={tableWrapperRef}
            data-automation="team-detail-members-table"
        >
            <Table<TeamUserTableRow>
                data={tableData}
                columnConfig={columnConfig}
                pagination={{ pageSize: PAGE_SIZE }}
                withArrowButtons={false}
                sticky={{
                    offsetHeader: tableTopStickyOffsetPx,
                    getContainer: () =>
                        document.querySelector<HTMLElement>(`.${TABLE_STICKY_SCROLL_CONTAINER_CLASS}`) ?? document.body,
                }}
                selection={{
                    enabled: true,
                    columnWidth: SELECTION_COLUMN_WIDTH,
                    fixed: true,
                    selectedRowKeys: selectedUserIds,
                    onChange: (keys) => onSelectionChange(keys.map((key) => Number(key))),
                }}
            >
                <Table.TableTop borderBottom>
                    <Table.TableTop.Filters
                        filters={[
                            {
                                filter: "search",
                                initialValue: searchTerm,
                                onSearchChange: setSearchTerm,
                                placeholder: translate("si.management.teams.detail.search.placeholder"),
                            },
                        ]}
                        factoryFilters={
                            <Flex
                                flex="1"
                                justifyContent="flex-end"
                            >
                                <Button
                                    type="primary"
                                    onClick={onAddMembers}
                                    dataAutomation="team-detail-add-members-button"
                                >
                                    {translate("si.management.teams.detail.add_members")}
                                </Button>
                            </Flex>
                        }
                    />
                    <Table.TableTop.Selection
                        enabled
                        selectText={translate("si.management.teams.detail.selected_count")}
                        actionButtons={[
                            {
                                iconName: "delete",
                                children: translate("si.management.teams.detail.remove_from_team"),
                                onClick: onRemoveSelected,
                                isLoading: isRemoving,
                                isDisabled: isRemoving,
                                dataAutomation: "team-detail-remove-from-team",
                            },
                        ]}
                    />
                </Table.TableTop>
            </Table>
        </StyledTableCardWrapper>
    );
};
